import { useBenchmarkTakeoverStore } from "src/stores/benchmarkTakeoverStore";

// ---------------------------------------------------------------------------
// Benchmark stream interception. Both generation surfaces call
// `maybeInterceptBenchmarkChunk` on every structured chunk BEFORE their normal
// chunk handling:
//
//  - Draft: GeneratePanel's chapterGenerator onChunk. The server announces a
//    session with `{ benchmarkSessionId, benchmarkSurface: "draft",
//    comparisonMode: true }` and then streams text chunks tagged
//    `stream: "A" | "B"` (the normal comparison-mode envelope).
//  - Write: Card#streamWithIdToken (write plugin SSE). The server announces
//    with `{ benchmarkSessionId, benchmarkSurface: "write" }` and streams
//    `{ content: { index: 0 | 1, text } }` chunks (index 0 → A, 1 → B).
//
// Once a context is active every subsequent chunk of that stream is consumed
// here and routed into the benchmark takeover store, so the normal output UI
// (draft comparison cards / right-bar write card) never renders this
// generation.
// ---------------------------------------------------------------------------

const UNUSABLE_PAIR_MESSAGE = "We couldn't set up a fair comparison this time — your writing is saved in History.";

export function createBenchmarkInterceptContext({ surface, onWriteAgain = null, onAbort = null, card = null, precedingText = "" } = {}) {
  return {
    surface,
    onWriteAgain,
    // Write: the text the models continued from, for the takeover's context excerpt.
    precedingText,
    // Lets the takeover stop the generation when it is closed mid-stream.
    onAbort,
    card,
    // true once a benchmarkSessionId chunk has been seen for this stream
    active: false,
    // The session this stream opened. Every later write is checked against
    // the store's current session: the user can close the takeover (which
    // aborts the request, but buffered text and terminal callbacks can still
    // fire) and start another comparison before this stream is fully torn
    // down. A stale context must never touch the newer session.
    sessionId: null,
    // Set once the stream ended or failed; late chunks are swallowed so they
    // neither reach the takeover nor fall through to the normal output UI.
    finished: false,
    // set on Write Again replays: the stream is expected to be intercepted,
    // so ending without a session means the retry failed.
    expectSession: false,
    pendingSampleText: { A: "", B: "" },
    flushTimer: null,
  };
}

const STREAM_RENDER_INTERVAL_MS = 80;

/** Whether the store's current session is the one this context opened. */
function ownsSession(ctx) {
  return !!ctx?.active && !ctx.finished && ctx.sessionId !== null && useBenchmarkTakeoverStore.getState().sessionId === ctx.sessionId;
}

function cancelFlush(ctx) {
  if (ctx.flushTimer) {
    clearTimeout(ctx.flushTimer);
    ctx.flushTimer = null;
  }
}

function flushBenchmarkText(ctx) {
  if (!ctx) return;
  cancelFlush(ctx);
  if (!ownsSession(ctx)) {
    ctx.pendingSampleText = { A: "", B: "" };
    return;
  }
  const store = useBenchmarkTakeoverStore.getState();
  for (const sample of ["A", "B"]) {
    const text = ctx.pendingSampleText?.[sample] || "";
    if (text) {
      ctx.pendingSampleText[sample] = "";
      store.appendSampleText(sample, text);
    }
  }
}

function bufferBenchmarkText(ctx, sample, text) {
  ctx.pendingSampleText[sample] += text;
  if (ctx.flushTimer) return;
  // Rebuilding a multi-thousand-word prose DOM for every model token can lock
  // or crash the browser, especially while the user is scrolling. Render at a
  // steady cadence and synchronously flush once the stream ends.
  ctx.flushTimer = setTimeout(() => flushBenchmarkText(ctx), STREAM_RENDER_INTERVAL_MS);
}

/**
 * Returns true when the chunk belongs to a benchmark session and has been
 * consumed (the caller must skip its normal handling for that chunk).
 */
export function maybeInterceptBenchmarkChunk(chunk, ctx) {
  if (!chunk || typeof chunk !== "object" || !ctx) return false;
  let store = useBenchmarkTakeoverStore.getState();

  // A stream that already ended, or whose session was closed/replaced, has
  // nothing left to say. Consume its chunks so they are not rendered anywhere.
  if (ctx.finished) return true;
  if (ctx.active && !ownsSession(ctx)) {
    cancelFlush(ctx);
    ctx.pendingSampleText = { A: "", B: "" };
    return true;
  }

  if (!ctx.active) {
    if (!chunk.benchmarkSessionId) return false;
    // A Write Again replay belongs to the takeover that asked for it. If that
    // takeover was closed (or replaced) before the replay announced its
    // session, swallow the whole stream instead of reopening the comparison.
    if (ctx.expectSession && !awaitingReplay(store)) {
      finishContext(ctx);
      return true;
    }
    ctx.active = true;
    ctx.sessionId = chunk.benchmarkSessionId;
    store.beginSession({
      sessionId: chunk.benchmarkSessionId,
      surface: chunk.benchmarkSurface || ctx.surface,
      onWriteAgain: ctx.onWriteAgain,
      onAbort: ctx.onAbort,
      interceptedCard: ctx.card,
      precedingText: ctx.precedingText,
    });
    store = useBenchmarkTakeoverStore.getState();
    // Process any prose carried with the announcement as part of this frame.
  }

  const sample = chunk.stream === "A" || chunk.stream === "B" ? chunk.stream
    : ctx.surface === "write" && chunk.content?.index === 0 ? "A"
      : ctx.surface === "write" && chunk.content?.index === 1 ? "B" : null;
  if (chunk.benchmarkSampleReset === true) {
    if (sample && Number.isInteger(chunk.sampleRevision) && chunk.sampleRevision > store.sampleRevisions[sample]) {
      // Archive the last old tokens before resetting. The healthy side may
      // still be arriving in this same stream and must keep every token.
      flushBenchmarkText(ctx);
      store.resetSample(sample, chunk.sampleRevision);
    }
    return true;
  }
  // Untagged frames belong to the original candidate. A late provider callback
  // must not append prose or mark a replacement complete.
  if (sample && (chunk.sampleRevision ?? 0) !== store.sampleRevisions[sample]) return true;

  if (chunk.benchmarkUnusable) {
    // Flush the last tokens before entering recovery: when History could not
    // be saved, the visible samples may be the writer's only remaining copy.
    flushBenchmarkText(ctx);
    store.failSession(typeof chunk.message === "string" && chunk.message ? chunk.message : UNUSABLE_PAIR_MESSAGE, {
      preserveSamples: chunk.preserveSamples === true,
      refund: chunk.refund || null,
      historySaved: typeof chunk.historySaved === "boolean" ? chunk.historySaved : null,
    });
    return true;
  }

  if (chunk.benchmarkFallback) {
    flushBenchmarkText(ctx);
    store.fallbackToSample(chunk.fallbackPosition, chunk.refund || null, typeof chunk.historySaved === "boolean" ? chunk.historySaved : null);
    return true;
  }

  if (chunk.benchmarkSampleDone === true && (chunk.stream === "A" || chunk.stream === "B")) {
    flushBenchmarkText(ctx);
    store.markSampleDone(chunk.stream);
    return true;
  }

  if (chunk.error) {
    // Per-stream or global generation error → the blinded pair is unusable.
    flushBenchmarkText(ctx);
    store.failSession(typeof chunk.error === "string" ? chunk.error : undefined);
    if (chunk.stream === "A" || chunk.stream === "B") {
      // Strip per-stream errors so shared stream plumbing (streamRequest)
      // doesn't also treat them as fatal and double-toast.
      delete chunk.error;
    }
    return true;
  }

  if (ctx.surface === "write") {
    const content = chunk.content;
    if (content && typeof content === "object" && "index" in content && typeof content.text === "string") {
      const sample = content.index === 0 ? "A" : content.index === 1 ? "B" : null;
      if (sample) bufferBenchmarkText(ctx, sample, content.text);
    }
    // completionId / id / prompt chunks carry no user-visible output.
    return true;
  }

  // draft
  if (typeof chunk.text === "string" && (chunk.stream === "A" || chunk.stream === "B")) {
    bufferBenchmarkText(ctx, chunk.stream, chunk.text);
  }
  // comparisonMode / cardId / mode / incrementScene chunks are metadata the
  // takeover doesn't need; swallow them so the comparison-card UI stays shut.
  return true;
}

/** Call when the underlying stream ends (both samples finished streaming). */
export function benchmarkStreamEnded(ctx) {
  if (!ctx || ctx.finished) return;
  const store = useBenchmarkTakeoverStore.getState();
  if (ctx.active) {
    if (ownsSession(ctx)) {
      flushBenchmarkText(ctx);
      store.markGenerationDone();
    }
    finishContext(ctx);
  } else if (ctx.expectSession) {
    // A Write Again replay that the server chose not to intercept. Only the
    // takeover that is still waiting for that replay should hear about it.
    if (awaitingReplay(store)) store.failSession();
    finishContext(ctx);
  }
}

/** Call when the underlying stream errors out. */
export function benchmarkStreamFailed(ctx, error) {
  if (!ctx || ctx.finished) return;
  const store = useBenchmarkTakeoverStore.getState();
  const message = failureMessage(error);
  if (ctx.active) {
    if (ownsSession(ctx)) {
      flushBenchmarkText(ctx);
      store.failSession(message);
    }
    finishContext(ctx);
  } else if (ctx.expectSession) {
    if (awaitingReplay(store)) store.failSession(message);
    finishContext(ctx);
  }
}

// The stream transport already turns server payload errors (content policy,
// credit limits, benchmark unavailable) into user-facing messages; show
// those instead of the generic line. An abort has no message worth showing.
function failureMessage(error) {
  const message = typeof error === "string" ? error : typeof error?.message === "string" ? error.message : "";
  const trimmed = message.trim();
  if (!trimmed || error?.cause?.name === "AbortError" || /\babort/i.test(trimmed)) return undefined;
  return trimmed;
}

// `writeAgain` resets the takeover into `loading` with no session yet; that
// is the only state in which an un-intercepted replay is the takeover's news.
function awaitingReplay(store) {
  return store.stage === "loading" && !store.sessionId;
}

function finishContext(ctx) {
  cancelFlush(ctx);
  ctx.pendingSampleText = { A: "", B: "" };
  ctx.finished = true;
}

export function isBenchmarkIntercepted(ctx) {
  return !!ctx?.active;
}
